import React from 'react'

import type { NumberFormatValues } from 'react-number-format'
import { NumericFormat } from 'react-number-format'
import { connect } from 'react-redux'

import type { Locale } from '@libs/typescript-utils'

import { getDecimal } from '@helpers'

import { useNumberFormatSeparators } from '@hooks'

import { INVOICE_AMOUNT_INPUT_DECIMAL_PLACES } from '@constants'

import type { TextFieldBehaviorProps } from '../FormElements/TextFieldBehavior'
import { TextFieldBehavior } from '../FormElements/TextFieldBehavior'

export type AmountInputProps = Omit<TextFieldBehaviorProps, 'onChange' | 'value'> &
  Omit<
    React.ComponentProps<typeof NumericFormat>,
    | 'autoComplete'
    | 'decimalScale'
    | 'decimalSeparator'
    | 'fixedDecimalScale'
    | 'getInputRef'
    | 'valueIsNumericString'
    | 'onBlur'
    | 'onChange'
    | 'onFocus'
    | 'onValueChange'
    | 'thousandSeparator'
    | 'type'
  > & {
    locale: Locale
    maximumFractionDigits?: number
    minimumFractionDigits?: number
    onChange: (value: string) => void
  }

function PureAmountInput({
  locale,
  maximumFractionDigits = INVOICE_AMOUNT_INPUT_DECIMAL_PLACES,
  minimumFractionDigits = 0,
  name,
  onChange,
  ...rest
}: AmountInputProps) {
  const { decimalSeparator, thousandSeparator } = useNumberFormatSeparators(locale)

  const handleValueChange = React.useCallback(
    ({ value, floatValue }: NumberFormatValues) => {
      const inputValue =
        floatValue === undefined
          ? value
          : getDecimal(floatValue, {
              maximumFractionDigits,
              minimumFractionDigits,
            })
      onChange(inputValue)
    },
    [maximumFractionDigits, minimumFractionDigits, onChange]
  )

  return (
    <NumericFormat
      {...rest}
      autoComplete="off"
      customInput={TextFieldBehavior}
      decimalScale={maximumFractionDigits}
      decimalSeparator={decimalSeparator}
      fixedDecimalScale={minimumFractionDigits === maximumFractionDigits && minimumFractionDigits !== 0}
      valueIsNumericString
      name={name}
      onValueChange={handleValueChange} // we need the raw value instead of formatted (event.target.value), use decimal numeric string as BE provide this data to handle dirty fields properly
      thousandSeparator={thousandSeparator}
      type="tel"
    />
  )
}

export const AmountInput: React.FC<Omit<AmountInputProps, 'locale'>> = connect(
  (state: Store) => ({
    locale: state.locale.language,
  }),
  {} // do not pass "dispatch" to props - caused issue in "rest"
)(PureAmountInput)

AmountInput.displayName = 'AmountInput'
